import { APGCovid19Constants } from './model/apg-newview-constants';
import { CovidSamples } from './model/covid-sample';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams} from '@angular/common/http';
import { environment } from "../../environments/environment";
import { timeout, tap, map, catchError } from 'rxjs/operators';
import { Observable, throwError} from 'rxjs';
import { Covid19Batch, WellType } from './model/covid19Batch';
import { KitsDTO } from '../masterdata/models/kits-dto';
import { Comment } from './model/comment';
import { SplitBatchDTO } from './model/split-batch';
import { BBResponseContainer } from '../common-services/response-container';
import { ManualQCReviewRequest } from './model/manual-qc-review-request';
import { ZiathRequest } from '../models/ziathRequest';


@Injectable()
export class APGTestingService {

  defaultTimeout = 600000;

  readonly PROCESSMMDMSERVICE = 'admin/mdm/result-process';
  constructor(private http: HttpClient) {
  }

  getBatchesForLab(labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/list";
    let requestParam = new HttpParams().set('lab', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getBatchForLabAndBatchId(labId: string, fieldName: string , value:string ): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch";
    let requestParam = new HttpParams().set('lab', labId).set('fieldName', fieldName).set('value', value);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  saveSampleData(batch: Covid19Batch, noOfPositivePCRControls: number, noOfNegativePCRControls): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch?noOfPositivePCRControls=1&noOfNegativePCRControls=1";
     return this.http.post<BBResponseContainer>(url, batch).pipe(timeout(environment.defaultTimeout), tap({next: event => { },error: error => {
       error.url = url;
    }}));
  }

  filterBatches(selectedStates: string, selectedLabId: string, typeOfPage: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/filter";
    let requestParam = new HttpParams().set('selectedStatusList', selectedStates).set('labId', selectedLabId).set('typeOfPage', typeOfPage);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  uploadExcelSheet(genericFormData: FormData): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "api/excel/upload";
    return this.http.post<any>(url, genericFormData).pipe(timeout(this.defaultTimeout),tap({next: event => { },error: error => {
          error.url = url;
    }}));
  }

  uploadExcelSheetMidplex(formData: FormData): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/uploadMidplexSamples";
    return this.http.post<any>(url, formData).pipe(timeout(this.defaultTimeout), tap(error => {
      error.url = url;
    }));
  }

  uploadFLXFile(formData: FormData) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/articResultUpload";
        return this.http.post<any>(url, formData).pipe(timeout(this.defaultTimeout), tap(error => {
      error.url = url;
    }));
  }

  uploadFLXFileQPCRBiorad(formData: FormData) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/maResultUpload/biorad";
    return this.http.post<any>(url, formData).pipe(timeout(this.defaultTimeout), tap(error => {
      error.url = url;
    }));
  }

  getPlateViewData(batchId: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/batchIdForPlateView";
    let requestParam = new HttpParams().set('batchId', batchId).set('labId', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getBatchWithSample(sampleId: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/getBatchFromSampleId";
    let requestParam = new HttpParams().set('sampleId', sampleId).set('labId', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  excel(batch: Covid19Batch) {
    return this.http.get(environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/downloadExcel?batchId=' + batch.batchID + '&externalIdentification=' + batch.externalIdentification+'&sender='+ batch.sender +
    '&labID='+ batch.lab, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }),  catchError(error => throwError(null))
    );
  }

  getMachinesFromMDM(labId: string, allowedExtractors: string, allowedCyclers: string): Observable<BBResponseContainer> {
    let url = environment.BB_MDMSERVICE_URL + 'admin/mdm/machine/extractorAndCyclerTypes/' + labId + '/' + true;
    let requestParam = new HttpParams().set('manufacturerForExtractor', allowedExtractors).set('manufacturerForCycler', allowedCyclers);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getKitsFromMDM(labID: string, swabRestriction: string, extractorKits: string, pcrKits: string): Observable<BBResponseContainer> {
    let url = environment.BB_MDMSERVICE_URL + 'admin/mdm/kits/kitTypes' + '/' + labID + '/' + swabRestriction;
    let requestParam = new HttpParams().set('extractorKits', extractorKits).set('pcrKits', pcrKits);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  updateKitInMDM(kitsDTO: KitsDTO): Observable<BBResponseContainer> {
    let url = environment.BB_MDMSERVICE_URL + 'mdm/kits/chemicalInfo';
    return this.http.put<any>(url, kitsDTO).pipe(timeout(this.defaultTimeout), tap({next: event => { },error: error => {
      error.url = url;
    }}));
  }


  getBatchWithSampleUnboxing(sampleId: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/getBatchFromSampleIdForUnboxingStep";
    let requestParam = new HttpParams().set('sampleId', sampleId).set('labId', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  validateEurofinsBarcode(scannedEurofinsBarcode: string, labId: string, isBarcodeValidationRequired: boolean): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/validate/barcode";
    let requestParam = new HttpParams().set('scannedBarcode', scannedEurofinsBarcode).set('labId', labId).set('isBarcodeExistanceCheckRequired', isBarcodeValidationRequired.toString());
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  filterBatchesForSamplesMenu(selectedStates: string, selectedLabId: string, fromDate: string, toDate: string, typeOfPage: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/filter";
    let requestParam = new HttpParams().set('selectedStatusList', selectedStates).set('labId', selectedLabId).set('fromDate', fromDate).set('toDate', toDate).set('typeOfPage', typeOfPage);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  uploadResultFile(formData: FormData, batchID: string, gene: string, dominantChannel: string, labId: string, rtPCRKitVendor: string, machineIOCapcity : string, fileType : string): Observable<BBResponseContainer> {

    let url = "";
    let requestParam = new HttpParams();
    if ( !gene || gene.trim()=="" ) {
      url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/upload/result/" + labId + "/" + batchID;
    } else if (fileType == APGCovid19Constants.fastFinder){
      requestParam = requestParam.set('fileType',fileType);
      url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/fastFinderUpload/result/" + labId + "/" + batchID;
    }
    else {
      requestParam = requestParam.set('gene', gene).set('dominantChannel', dominantChannel).set('rtPCRKitVendor', rtPCRKitVendor).set('machineIOCapcity', machineIOCapcity).set('fileType',fileType);
      url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/upload/result/" + labId + "/" + batchID;
    }
    const options = url ? { params: requestParam } : {};
    return this.http.post<BBResponseContainer>(url, formData, options).pipe(timeout(this.defaultTimeout), tap({next: event => { },error: error => {
      error.url = url;
    }}));
  }
  updateSamples(batchID: string, sampleToUpdate: CovidSamples, labId: string, wellType: string, sender:string, rushSample:boolean ): Observable<BBResponseContainer> {
   let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/sample/' + labId + '/' + batchID + '/' + wellType + '/' + sender + '/' + rushSample;
    return this.http.put<BBResponseContainer>(url, sampleToUpdate).pipe(timeout(environment.defaultTimeout));
  }

  confirmPlates(selectedBatch: Covid19Batch,reportingOutSideBB: boolean): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/generate/csv/"+reportingOutSideBB;
     return this.http.post<BBResponseContainer>(url, selectedBatch).pipe(timeout(environment.defaultTimeout), tap({next: event => { },error: error => {
       error.url = url;
    }}));
  }

  downloadResultFile(batchId: string, labId: string) {
    return this.http.get(environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/result/download/' + labId + '?batchId=' + batchId, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }),  catchError(error => throwError(null))
    );
  }

  validateScannedBarcode(barcode: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/validate/" + barcode;
    let requestParam = new HttpParams().set('labId', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  saveScannedSamples(selectedBatch: Covid19Batch): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/samples/" + selectedBatch.batchID;
    return this.http.patch<BBResponseContainer>(url, selectedBatch).pipe(timeout(this.defaultTimeout), tap({next: event => { },error: error => {
      error.url = url;
    }}));
  }

  saveContaminationInformation(batchID: string, sampleToUpdate: CovidSamples, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/sample/contaminationInfo/" + batchID + "/" + labId;
    return this.http.patch<BBResponseContainer>(url, sampleToUpdate).pipe(timeout(this.defaultTimeout), tap({next: event => { },error: error => {
      error.url = url;
    }}));
  }

  filledExcel(data: string, labId: string) {
    let urlString = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/downloadFilledExcel?batchId=' + data + '&labId=' + labId;
    return this.http.get(urlString, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let response = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return response;
      }),  catchError(error => throwError(null))
    );
  }

  undoMerge(batch: Covid19Batch) : Observable<BBResponseContainer> {

    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/undoMerge" + "/"+batch.lab;

    return this.http.patch<BBResponseContainer>(url, batch).pipe(timeout(environment.defaultTimeout), tap({next: event => { },error: error => {
      error.url = url;
    }}));
  }


  getStorageForBatch(labId: string, batchId: string): Observable<BBResponseContainer> {
    let url = environment.BB_STORAGE_URL + 'storage/getBatchStorageLocation/' + batchId + '/' + labId;
    let requestParam = new HttpParams().set('lab', labId).set('batchID', batchId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  saveComment(batchID: string, labId: string, comments: Comment[]): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/comment/"+batchID+"/"+labId;
    return this.http.patch<BBResponseContainer>(url, comments).pipe(timeout(environment.defaultTimeout));
  }

  resultReportExcelDowload(batchID: string, labId: string) {
    let urlString = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/export/report/' + labId + '/' + batchID;
    return this.http.get(urlString, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let response = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return response;
      }),  catchError(error => throwError(null))
    );
  }

  getPCRWorkfileForBiorad(batchID: string, labId: string, redoBatches: string[], boxIdRequired: boolean) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/specialpcrworkfile/biorad?batchID=' + batchID + '&labId=' + labId + '&redoBatches=' + redoBatches + '&boxIdRequired=' + boxIdRequired;
    return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }),  catchError(error => throwError(null))
    );
  }
  externalFileTransfer(selectedBatch: Covid19Batch): Observable<BBResponseContainer> {

    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/external/upload";
    return this.http.post<BBResponseContainer>(url, selectedBatch).pipe(timeout(this.defaultTimeout), tap({next: event => { },error: error => {
      error.url = url;
      }}));

  }

  getProcessList(labId : string,  mergeGroup : string): Observable<BBResponseContainer> {
    let url = environment.BB_MDMSERVICE_URL + this.PROCESSMMDMSERVICE + "/" + labId + "/" + mergeGroup;
    return this.http.get<BBResponseContainer>(url).pipe(tap({next: event => { },error: error => {
        error.url = url;
    }}));
  }

  getWorkfileForBioRadLims(batchID: string, labId: string, redoBatches: string[], kit: any) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/specialpcrworkfile/bioradlims?batchID=' + batchID + '&labId=' + labId + '&redoBatches=' + redoBatches;
    return this.http.put(url, kit, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }),  catchError(error => throwError(null))
    );
  }

  getWorkfileForFLA_ABI(batchID: string, labId: string, redoBatches: string[]) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/specialpcrworkfile/flaabi";

    let requestParam = new HttpParams().set('batchID', batchID).set('labId', labId).set('redoBatches', redoBatches.join(','));
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getContactIdForBarcode(eurofinsBarcode: string) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/shippedBarcode/contactId";
    let requestParam = new HttpParams().set('eurofinsBarcode', eurofinsBarcode);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }


  splitBatch(batchID: string, labId: string, splitBatchDTO: SplitBatchDTO): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/splitbatch/"+batchID+"/"+labId;
    return this.http.patch<BBResponseContainer>(url, splitBatchDTO).pipe(timeout(environment.defaultTimeout),  tap({next: event => { },error: error => {
      error.url = url;
      }}));
  }
  getResultConfigBykitCycler(kitLot: string, machineName1: string, lab: string) {
    let url = environment.BB_MDMSERVICE_URL + "resultConfig/getByKitCycler/"+lab;
    let requestParam = new HttpParams().set('kit', kitLot).set('cycler',machineName1);
    const options = url ? { params: requestParam } : {};
    return this.http.get<any>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getSampleResultHistory(batchId: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/sampleResultHistory';
    let requestParam = new HttpParams().set('batchId', batchId).set('labId', labId)
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout),  tap({next: event => { },error: error => {
      error.url = url;
    }}));
  }



  getUploadedFileCountForCovidBatch(lab: string, batchID: string) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/filesPerManufacturer/" + lab + "/" + batchID;
    return this.http.get<any>(url).pipe(timeout(environment.defaultTimeout));
  }

  getBatchHistory(lab: string, batchID: string, wellType: WellType, sourceBatchIds: string) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/history/" + lab + "/" + batchID + "/" + wellType;
    let requestParam = new HttpParams().set('sourceBatchIds', sourceBatchIds)
    const options = url ? { params: requestParam } : {};
    return this.http.get<any>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getWorkFile(batchId: string,labId: string){
    let urlString = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19ArchiveBatch/workFile/"+batchId+"/"+labId;
    return this.http.get(urlString, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let response = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return response;
      }),  catchError(error => throwError(null))
    );
  }

  getUnclippedSamples(lab: string, unboxedSampleBarcodes: Array<string>): Observable<any> {
    const url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/unclipped/" + lab;
    return this.http.post<any>(url, unboxedSampleBarcodes).pipe(timeout(environment.defaultTimeout));
  }

  redoBatchService(redoBatch: Covid19Batch): Observable<any> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/redoForNGS";
    return this.http.put<BBResponseContainer>(url, redoBatch).pipe(timeout(environment.defaultTimeout), tap( error => {
      error.url = url;
    }));
  }


  downloadFLXWorkFile(batchId: string, labId: string) {
    return this.http.get(environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/flxWorkFile/' + labId + '?batchID=' + batchId, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }),  catchError(error => throwError(null))
    );
  }

  getqPCRWorkfile(batchID: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/pcrworkfile";
    let requestParam = new HttpParams().set('batchID', batchID).set('labId', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getqPCRBioRadWorkfile(batchID: string, labId: string){
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/pcrworkfile/biorad";
    let requestBody = {'batchID':batchID, 'labId':labId};
    return this.http.put(url, requestBody ,{observe: 'response', responseType: 'blob'}).pipe(
      map((res) => {
        return {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: batchID+".csv"
        }
      }), catchError(error => throwError(null))
    );
  }

  getLibQCResults(batchID: string, labId: string){
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/libQCResults";
    let requestBody = {'batchID':batchID, 'labId':labId};
    return this.http.put(url, requestBody, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }), catchError(error => throwError(() => new Error('getLibQCResults : An error occurred')))
    );
  }


  downloadDNAWorkFile(batchID: string,lab: string){
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/result/downloadNormalizationWorkfile/'+ batchID+'/'+lab+'/'+'DNA';
    return this.http.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
  }

  downloadBufferWorkFile(batchID: string,lab: string){
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/result/downloadNormalizationWorkfile/'+ batchID+'/'+lab+'/'+'Buffer';
    return this.http.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
  }


  uploadSamplesExcelSheet(formData: FormData): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/uploadSamples";
    return this.http.post<any>(url, formData).pipe(timeout(this.defaultTimeout), tap( error => {
      error.url = url;
    }));
  }

  saveTagsExcel(formData: FormData): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/tagSystem/upload";
    return this.http.post<any>(url, formData).pipe(timeout(environment.defaultTimeout), tap( error => {
        error.url = url;
    }));
    }

  downloadSecondaryAnalysisFile(folderPath:string,fileName:string) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/secondaryAnalysis/downloadFiles/'+folderPath+"/"+fileName;
    return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        return res;
      }), catchError(() => throwError(() => new Error('downloadSecondaryAnalysisFile : An error occurred')))
    );
  }

  saveManualQCReviews(manualQCReviewRequest: ManualQCReviewRequest) : Observable<any> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/manualQCReview";
    return this.http.patch<any>(url, manualQCReviewRequest).pipe(timeout(environment.defaultTimeout), tap( error => {
        error.url = url;
    }));
  }
  getLatestBatchIdBySender(labId:string, selectedSender: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/batch/" + labId + "/" + selectedSender;
    return this.http.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
  }

  getBatchViewForQCReport(lab: string,batchID: string ) {

    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/batchView/" + lab + "/" + batchID;
    return this.http.get<any>(url).pipe(timeout(environment.defaultTimeout));
  }

  downloadcDNAControlsWorkFile(batch: Covid19Batch): Observable<any> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/downloadcDNAControlsWorkfile?batchId='+ batch.batchID+
    '&labID=' + batch.lab;
    return this.http.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout), tap( error => {
      error.url = url;
    }));
  }

  getZiatScanData(ziathRequest : ZiathRequest): Observable<any> {
    let url = environment.ZIATH_SCAN_URL;
    return this.http.post<BBResponseContainer>(url, ziathRequest).pipe(timeout(environment.defaultTimeout),  tap({next: event => { },error: error => {
      error.url = url;
    }}));
  }

  downloadNormalizationWorkFile(batchID: string,lab: string){
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/result/downloadNormalizationWorkfileMicroArray/'+ batchID+'/'+lab;
    return this.http.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
  }

  downloadCleanUpWorkfile(batchID: string, lab: string){
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19Batch/cleanUpWorkfile/'+ batchID+'/'+lab;
    return this.http.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
  }

  uploadQubitFile(formData: FormData) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19Batch/uploadQubitFile";
    return this.http.post<any>(url, formData)
  }
}
